import { REPEAT_END_TYPE_OPTIONS } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/session-repeat-rule-editor/session-repeat-rule-editor-constants";
import { DAYS_OF_WEEK_LOWERCASE_LENGTH_3 } from "constants/daysOfWeek";
import moment from "moment-timezone";
import getNextEventOnNthDayOfWeekInMonthMoment from "./getNthDayOfWeekInMonth";

const getIntervalsRepeatedOnNthDayOfWeekInMonth = ({
  repeatRule,
  template,
  timezone,
}: {
  repeatRule: RepeatRule;
  template: BlockedInterval;
  timezone: string;
}) => {
  if (
    repeatRule.repeat_interval !== "month" ||
    !repeatRule.repeat_on_nth_day_of_week_in_month
  ) {
    throw new Error(
      "getAppointmentsRepeatedOnNthDayOfWeekInMonth can only be called for events repeating monthly on nth day of week in the month",
    );
  }

  const blockedIntervals: BlockedInterval[] = [];

  const { start: templateStart, end: templateEnd } = template;

  let dateCursor = moment.tz(templateStart, timezone);
  const {
    repeat_every,
    repeat_on_nth_day_of_week_in_month: { nth, day_of_week },
  } = repeatRule;

  const templateLength = moment
    .tz(templateEnd, timezone)
    .diff(dateCursor, "minute");

  if (repeatRule.repeat_end.type === REPEAT_END_TYPE_OPTIONS.end_date.id) {
    const endDate = moment
      .tz(repeatRule.repeat_end.end_date, timezone)
      .endOf("day");

    do {
      const end = dateCursor.clone().add(templateLength, "minute");
      blockedIntervals.push({
        ...template,
        start: dateCursor.toISOString(),
        end: end.toISOString(),
      });

      dateCursor = getNextEventOnNthDayOfWeekInMonthMoment({
        currentEventStart: dateCursor,
        nth: nth,
        dayOfWeek: day_of_week,
        skipMonths: repeat_every,
        timezone: timezone,
      });
    } while (dateCursor.isBefore(endDate));

    return blockedIntervals;
  }

  for (let i = 0; i < repeatRule.repeat_end.end_after; i++) {
    const end = dateCursor.clone().add(templateLength, "minute");
    blockedIntervals.push({
      ...template,
      start: dateCursor.toISOString(),
      end: end.toISOString(),
    });

    dateCursor = getNextEventOnNthDayOfWeekInMonthMoment({
      currentEventStart: dateCursor,
      nth: nth,
      dayOfWeek: day_of_week,
      skipMonths: repeat_every,
      timezone: timezone,
    });
  }
  return blockedIntervals;
};

/**
 * Get blocked intervals by repeat rule for creating unavailable series
 */
export function getBlockedIntervalsByRepeatRules({
  repeatRule,
  timezone,
  template,
}: {
  repeatRule: RepeatRule;
  timezone: string;
  template: BlockedInterval;
}): BlockedInterval[] {
  const res: BlockedInterval[] = [];

  const { start: templateStart, end: templateEnd, ...restTemplate } = template;

  // Repeat by year or month
  if (
    repeatRule.repeat_interval === "year" ||
    repeatRule.repeat_interval === "month"
  ) {
    if (
      repeatRule.repeat_interval === "month" &&
      repeatRule.repeat_on_nth_day_of_week_in_month
    ) {
      return getIntervalsRepeatedOnNthDayOfWeekInMonth({
        repeatRule,
        template,
        timezone,
      });
    }

    let count = 0;

    // Determine the count based on repeat type
    if (repeatRule.repeat_end.type === REPEAT_END_TYPE_OPTIONS.end_date.id) {
      const startMoment = moment.tz(template.start, timezone);
      const endMoment = moment.tz(repeatRule.repeat_end.end_date, timezone);
      count = endMoment.diff(startMoment, repeatRule.repeat_interval);
    } else if (
      repeatRule.repeat_end.type === REPEAT_END_TYPE_OPTIONS.end_after.id
    ) {
      count = repeatRule.repeat_end.end_after;
    }

    for (let i = 0; i < count; i++) {
      const start = moment
        .tz(templateStart, timezone)
        .add(i * repeatRule.repeat_every, repeatRule.repeat_interval);
      const end = moment
        .tz(templateEnd || templateStart, timezone)
        .add(i * repeatRule.repeat_every, repeatRule.repeat_interval);
      res.push({
        ...restTemplate,
        start: start.toISOString(),
        end: end.toISOString(),
      });
    }

    return res;
  }

  // Repeat weekly
  return getBlockedIntervalsRepeatByWeekByRepeatRules({
    timezone,
    templateForWeek: getWeeklyBlockedIntervalsTemplate({
      template,
      timezone,
      repeatOnDays: repeatRule.repeat_on_days,
    }),
    startValue: +new Date(template.start),
    repeatEvery: repeatRule.repeat_every,
    repeatRule,
  });
}

/**
 * Get a week's blocked intervals by repeat-on-days settings
 */
function getWeeklyBlockedIntervalsTemplate({
  template: { start: templateStart, end: templateEnd, ...restTemplate },
  repeatOnDays,
  timezone,
}: {
  template: BlockedInterval;
  repeatOnDays: string[];
  timezone: string;
}): BlockedInterval[] {
  return DAYS_OF_WEEK_LOWERCASE_LENGTH_3.reduce((prev, curr, index) => {
    const weekday = moment.tz(templateStart, timezone).days();
    const weekStartWithTime = moment
      .tz(templateStart, timezone)
      .add(-weekday, "days")
      .toISOString();
    const includesDay = repeatOnDays.includes(curr);

    if (includesDay) {
      const start = moment
        .tz(weekStartWithTime, timezone)
        .add(index, "day")
        .toISOString();
      const end = moment
        .tz(weekStartWithTime, timezone)
        .add(index, "day")
        .add(moment.duration(moment(templateEnd).diff(templateStart)))
        .toISOString();

      prev.push({
        ...restTemplate,
        start,
        end,
      });
    }

    return prev;
  }, [] as BlockedInterval[]);
}

/**
 * Get blocked intervals repeated by weeks based on repeat rule
 */
function getBlockedIntervalsRepeatByWeekByRepeatRules({
  startValue,
  templateForWeek,
  repeatEvery,
  timezone,
  repeatRule,
}: {
  startValue: number;
  templateForWeek: BlockedInterval[];
  repeatEvery: number;
  timezone: string;
  repeatRule: RepeatRule;
}): BlockedInterval[] {
  const res: BlockedInterval[] = [];

  let i = 0;
  if (repeatRule.repeat_end.type === REPEAT_END_TYPE_OPTIONS.end_after.id) {
    while (res.length < repeatRule.repeat_end.end_after) {
      for (const interval of templateForWeek) {
        const nextInterval = getNthWeekBlockedInterval({
          interval,
          timezone,
          weekCount: i * repeatEvery,
        });

        if (
          +new Date(nextInterval.start) >= startValue &&
          res.length < repeatRule.repeat_end.end_after
        ) {
          res.push(nextInterval);
        }
      }
      i++;
    }
  } else if (
    repeatRule.repeat_end.type === REPEAT_END_TYPE_OPTIONS.end_date.id
  ) {
    const endDateValue = +new Date(repeatRule.repeat_end.end_date);
    let stop = false;
    while (!stop) {
      for (const interval of templateForWeek) {
        const nextInterval = getNthWeekBlockedInterval({
          interval,
          timezone,
          weekCount: i * repeatEvery,
        });

        if (+new Date(nextInterval.start) >= endDateValue) {
          stop = true;
        }

        if (
          +new Date(nextInterval.start) >= startValue &&
          +new Date(nextInterval.end) <= endDateValue
        ) {
          res.push(nextInterval);
        }
      }
      i++;
    }
  }

  return res;
}

/**
 * Get nth week blocked interval
 */
function getNthWeekBlockedInterval({
  interval,
  timezone,
  weekCount,
}: {
  interval: BlockedInterval;
  timezone: string;
  weekCount: number;
}): BlockedInterval {
  const start = moment(interval.start)
    .tz(timezone)
    .add(weekCount, "week")
    .toISOString();
  const end = moment(interval.end)
    .tz(timezone)
    .add(weekCount, "week")
    .toISOString();

  return {
    ...interval,
    start,
    end,
  };
}
